import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  useTheme,
  useScrollTrigger,
  Slide,
  Fab,
  Chip,
  Stack,
  Divider,
  Link,
  IconButton,
  Menu,
  MenuItem,
  alpha,
} from '@mui/material';
import {
  Home,
  People,
  Favorite,
  Notifications,
  Analytics,
  Security,
  ArrowUpward,
  Login,
  PersonAdd,
  Menu as MenuIcon,
  Close,
  CheckCircle,
  TrendingUp,
  Speed,
  Verified,
  Business,
  Phone,
  Email,
  LocationOn,
  Facebook,
  Twitter,
  LinkedIn,
  Instagram,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { setLanguage, getLanguage, t, Language } from '@/utils/i18n';
import LanguageSwitcher from '../components/LanguageSwitcher';

interface Props {
  window?: () => Window;
  children: React.ReactElement;
}

function HideOnScroll(props: Props) {
  const { children, window } = props;
  const trigger = useScrollTrigger({
    target: window ? window() : undefined,
  });

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

const AgenciesLandingPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [language, setLang] = useState<Language>(getLanguage());
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLanguageChange = (lang: Language) => {
    setLang(lang);
    setLanguage(lang);
    setAnchorEl(null);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const features = [
    {
      icon: <Favorite sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: t('feature1Title'),
      description: t('feature1Desc'),
    },
    {
      icon: <People sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: t('feature2Title'),
      description: t('feature2Desc'),
    },
    {
      icon: <Home sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: t('feature3Title'),
      description: t('feature3Desc'),
    },
    {
      icon: <Notifications sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: t('feature4Title'),
      description: t('feature4Desc'),
    },
    {
      icon: <Analytics sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: t('feature5Title'),
      description: t('feature5Desc'),
    },
    {
      icon: <Security sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: t('feature6Title'),
      description: t('feature6Desc'),
    },
  ];

  const steps = [
    {
      number: '1',
      title: t('step1Title'),
      description: t('step1Desc'),
    },
    {
      number: '2',
      title: t('step2Title'),
      description: t('step2Desc'),
    },
    {
      number: '3',
      title: t('step3Title'),
      description: t('step3Desc'),
    },
  ];

  const stats = [
    { value: '10K+', label: language === 'en' ? 'Properties Listed' : 'Propriétés Listées' },
    { value: '5K+', label: language === 'en' ? 'Happy Clients' : 'Clients Satisfaits' },
    { value: '98%', label: language === 'en' ? 'Match Accuracy' : 'Précision de Correspondance' },
    { value: '24/7', label: language === 'en' ? 'Support' : 'Support' },
  ];

  return (
    <Box sx={{ flexGrow: 1, bgcolor: 'background.default' }}>
      {/* Premium Navigation Bar */}
      <HideOnScroll>
        <AppBar 
          position="fixed" 
          sx={{ 
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            color: 'text.primary', 
            boxShadow: '0 2px 20px rgba(0,0,0,0.08)',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Container maxWidth="xl">
            <Toolbar sx={{ py: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                <Typography 
                  variant="h5" 
                  component="div" 
                  sx={{ 
                    fontWeight: 800,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    mr: 4,
                  }}
                >
                  souk.immo
                </Typography>
                <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 3, alignItems: 'center' }}>
                  <Button 
                    color="inherit" 
                    onClick={() => scrollToSection('features')}
                    sx={{ fontWeight: 500 }}
                  >
                    {t('navFeatures')}
                  </Button>
                  <Button 
                    color="inherit" 
                    onClick={() => scrollToSection('how-it-works')}
                    sx={{ fontWeight: 500 }}
                  >
                    {t('navHowItWorks')}
                  </Button>
                  <Button 
                    color="inherit" 
                    onClick={() => scrollToSection('stats')}
                    sx={{ fontWeight: 500 }}
                  >
                    {language === 'en' ? 'Stats' : 'Statistiques'}
                  </Button>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <LanguageSwitcher />
                <Button
                  variant="text"
                  startIcon={<Login />}
                  onClick={() => navigate('/login')}
                  sx={{ display: { xs: 'none', sm: 'flex' }, fontWeight: 500 }}
                >
                  {t('navLogin')}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<PersonAdd />}
                  onClick={() => navigate('/login')}
                  sx={{ 
                    display: { xs: 'none', sm: 'flex' },
                    fontWeight: 600,
                    px: 3,
                    boxShadow: 2,
                  }}
                >
                  {t('navSignUp')}
                </Button>
                <IconButton
                  sx={{ display: { xs: 'flex', md: 'none' } }}
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                >
                  {mobileMenuOpen ? <Close /> : <MenuIcon />}
                </IconButton>
              </Box>
            </Toolbar>
          </Container>
        </AppBar>
      </HideOnScroll>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 64,
            left: 0,
            right: 0,
            bgcolor: 'background.paper',
            zIndex: 1300,
            boxShadow: 4,
            py: 2,
            display: { xs: 'block', md: 'none' },
          }}
        >
          <Container>
            <Stack spacing={1}>
              <Button fullWidth onClick={() => scrollToSection('features')}>
                {t('navFeatures')}
              </Button>
              <Button fullWidth onClick={() => scrollToSection('how-it-works')}>
                {t('navHowItWorks')}
              </Button>
              <Button fullWidth onClick={() => scrollToSection('stats')}>
                {language === 'en' ? 'Stats' : 'Statistiques'}
              </Button>
              <Divider />
              <Button fullWidth startIcon={<Login />} onClick={() => navigate('/login')}>
                {t('navLogin')}
              </Button>
              <Button fullWidth variant="contained" startIcon={<PersonAdd />} onClick={() => navigate('/login')}>
                {t('navSignUp')}
              </Button>
            </Stack>
          </Container>
        </Box>
      )}

      {/* Premium Hero Section */}
      <Box
        sx={{
          pt: { xs: 18, md: 22 },
          pb: { xs: 10, md: 16 },
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 50%, rgba(25, 118, 210, 0.1) 0%, transparent 50%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
                <Chip
                  label={language === 'en' ? 'AI-Powered' : language === 'fr' ? 'Alimenté par IA' : 'مدعوم بالذكاء الاصطناعي'}
                  color="primary"
                  sx={{ mb: 3, fontWeight: 600 }}
                />
                <Typography
                  variant="h1"
                  component="h1"
                  gutterBottom
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '2.5rem', md: '4rem' },
                    lineHeight: 1.1,
                    mb: 3,
                    fontFamily: '"Playfair Display", serif',
                    color: 'text.primary',
                  }}
                >
                  {t('heroTitle')}
                </Typography>
                <Typography
                  variant="h5"
                  color="text.secondary"
                  sx={{ 
                    mb: 4, 
                    fontSize: { xs: '1.1rem', md: '1.4rem' },
                    lineHeight: 1.6,
                    fontWeight: 400,
                  }}
                >
                  {t('heroSubtitle')}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate('/login')}
                    sx={{ 
                      px: 5, 
                      py: 1.8,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      boxShadow: 'none',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(197, 160, 89, 0.2)',
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.3s',
                    }}
                  >
                    {t('heroCta')}
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => scrollToSection('features')}
                    sx={{ 
                      px: 5, 
                      py: 1.8,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      borderWidth: 1,
                      '&:hover': {
                        borderWidth: 1,
                        transform: 'translateY(-2px)',
                        bgcolor: 'rgba(60, 62, 63, 0.02)',
                      },
                      transition: 'all 0.3s',
                    }}
                  >
                    {t('heroCtaSecondary')}
                  </Button>
                </Stack>
                <Box sx={{ mt: 4, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', fontFamily: '"Playfair Display", serif' }}>
                      98%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('navFeatures')}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', fontFamily: '"Playfair Display", serif' }}>
                      10K+
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {language === 'en' ? 'Properties' : language === 'fr' ? 'Propriétés' : 'عقارات'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', fontFamily: '"Playfair Display", serif' }}>
                      5K+
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {language === 'en' ? 'Clients' : language === 'fr' ? 'Clients' : 'عملاء'}
                    </Typography>
                  </Box>
                </Box>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    opacity: 0.9,
                    zIndex: 1,
                  },
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    zIndex: 2,
                    p: 6,
                    color: 'white',
                    textAlign: 'center',
                    minHeight: 400,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Home sx={{ fontSize: 120, mb: 2, opacity: 0.9 }} />
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
                    {language === 'en' ? 'Smart Property Matching' : 'Appariement Intelligent'}
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    {language === 'en' 
                      ? 'Find the perfect match for every client' 
                      : 'Trouvez la correspondance parfaite pour chaque client'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box id="stats" sx={{ py: { xs: 6, md: 8 }, bgcolor: 'primary.main', color: 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box id="features" sx={{ py: { xs: 10, md: 14 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h2"
              component="h2"
              gutterBottom
              sx={{ fontWeight: 800, mb: 2 }}
            >
              {t('featuresTitle')}
            </Typography>
            <Typography
              variant="h5"
              color="text.secondary"
              sx={{ maxWidth: 700, mx: 'auto' }}
            >
              {t('featuresSubtitle')}
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      p: 3,
                      transition: 'all 0.3s',
                      border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: 'none',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 12px 30px rgba(0,0,0,0.05)',
                        borderColor: 'primary.main',
                      },
                    }}
                  >
                    <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 1, fontFamily: '"Playfair Display", serif' }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                      {feature.description}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Box id="how-it-works" sx={{ py: { xs: 10, md: 14 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h2"
              component="h2"
              gutterBottom
              sx={{ fontWeight: 800, mb: 2 }}
            >
              {t('howItWorksTitle')}
            </Typography>
            <Typography
              variant="h5"
              color="text.secondary"
              sx={{ maxWidth: 700, mx: 'auto' }}
            >
              {t('howItWorksSubtitle')}
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {steps.map((step, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Box sx={{ textAlign: 'center', position: 'relative' }}>
                  <Box
                    sx={{
                      width: 100,
                      height: 100,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 3,
                      fontSize: '2.5rem',
                      fontWeight: 800,
                      boxShadow: '0 8px 24px rgba(25, 118, 210, 0.3)',
                    }}
                  >
                    {step.number}
                  </Box>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {step.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 10, md: 14 },
          bgcolor: 'primary.main',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
            <Typography
              variant="h2"
              component="h2"
              gutterBottom
              sx={{ fontWeight: 800, mb: 3 }}
            >
              {t('ctaTitle')}
            </Typography>
            <Typography
              variant="h5"
              sx={{ mb: 5, opacity: 0.95, lineHeight: 1.6 }}
            >
              {t('ctaSubtitle')}
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/login')}
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                px: 6,
                py: 2,
                fontSize: '1.2rem',
                fontWeight: 700,
                boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                '&:hover': {
                  bgcolor: 'grey.100',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 32px rgba(0,0,0,0.3)',
                },
                transition: 'all 0.3s',
              }}
            >
              {t('ctaButton')}
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Premium Footer */}
      <Box sx={{ bgcolor: 'grey.900', color: 'white', pt: 8, pb: 4 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            <Grid item xs={12} md={4}>
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
                souk.immo
              </Typography>
              <Typography variant="body2" sx={{ mb: 3, opacity: 0.8, lineHeight: 1.7 }}>
                {t('footerTagline')}
              </Typography>
              <Stack direction="row" spacing={2}>
                <IconButton sx={{ color: 'white' }}><Facebook /></IconButton>
                <IconButton sx={{ color: 'white' }}><Twitter /></IconButton>
                <IconButton sx={{ color: 'white' }}><LinkedIn /></IconButton>
                <IconButton sx={{ color: 'white' }}><Instagram /></IconButton>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                {language === 'en' ? 'Product' : 'Produit'}
              </Typography>
              <Stack spacing={1}>
                <Link href="#" color="inherit" sx={{ textDecoration: 'none', opacity: 0.8, '&:hover': { opacity: 1 } }}>
                  {t('navFeatures')}
                </Link>
                <Link href="#" color="inherit" sx={{ textDecoration: 'none', opacity: 0.8, '&:hover': { opacity: 1 } }}>
                  {t('navHowItWorks')}
                </Link>
                <Link href="#" color="inherit" sx={{ textDecoration: 'none', opacity: 0.8, '&:hover': { opacity: 1 } }}>
                  {t('navPricing')}
                </Link>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                {language === 'en' ? 'Company' : 'Entreprise'}
              </Typography>
              <Stack spacing={1}>
                <Link href="#" color="inherit" sx={{ textDecoration: 'none', opacity: 0.8, '&:hover': { opacity: 1 } }}>
                  {language === 'en' ? 'About' : 'À propos'}
                </Link>
                <Link href="#" color="inherit" sx={{ textDecoration: 'none', opacity: 0.8, '&:hover': { opacity: 1 } }}>
                  {language === 'en' ? 'Blog' : 'Blog'}
                </Link>
                <Link href="#" color="inherit" sx={{ textDecoration: 'none', opacity: 0.8, '&:hover': { opacity: 1 } }}>
                  {language === 'en' ? 'Careers' : 'Carrières'}
                </Link>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                {language === 'en' ? 'Support' : 'Support'}
              </Typography>
              <Stack spacing={1}>
                <Link href="#" color="inherit" sx={{ textDecoration: 'none', opacity: 0.8, '&:hover': { opacity: 1 } }}>
                  {language === 'en' ? 'Help Center' : 'Centre d\'aide'}
                </Link>
                <Link href="#" color="inherit" sx={{ textDecoration: 'none', opacity: 0.8, '&:hover': { opacity: 1 } }}>
                  {language === 'en' ? 'Contact' : 'Contact'}
                </Link>
                <Link href="#" color="inherit" sx={{ textDecoration: 'none', opacity: 0.8, '&:hover': { opacity: 1 } }}>
                  {language === 'en' ? 'Privacy' : 'Confidentialité'}
                </Link>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                {language === 'en' ? 'Contact' : 'Contact'}
              </Typography>
              <Stack spacing={1.5}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: 0.8 }}>
                  <Email fontSize="small" />
                  <Typography variant="body2">support@realestatesmartai.com</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: 0.8 }}>
                  <Phone fontSize="small" />
                  <Typography variant="body2">+1 (555) 123-4567</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: 0.8 }}>
                  <LocationOn fontSize="small" />
                  <Typography variant="body2">
                    {language === 'en' ? 'San Francisco, CA' : 'San Francisco, CA'}
                  </Typography>
                </Box>
              </Stack>
            </Grid>
          </Grid>
          <Divider sx={{ my: 6, borderColor: 'rgba(255,255,255,0.1)' }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              © {new Date().getFullYear()} souk.immo. {t('footerRights')}.
            </Typography>
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Link href="#" color="inherit" sx={{ textDecoration: 'none', opacity: 0.7, fontSize: '0.875rem', '&:hover': { opacity: 1 } }}>
                {language === 'en' ? 'Terms' : 'Conditions'}
              </Link>
              <Link href="#" color="inherit" sx={{ textDecoration: 'none', opacity: 0.7, fontSize: '0.875rem', '&:hover': { opacity: 1 } }}>
                {language === 'en' ? 'Privacy' : 'Confidentialité'}
              </Link>
              <Link href="#" color="inherit" sx={{ textDecoration: 'none', opacity: 0.7, fontSize: '0.875rem', '&:hover': { opacity: 1 } }}>
                {language === 'en' ? 'Cookies' : 'Cookies'}
              </Link>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <Fab
          color="primary"
          size="medium"
          onClick={scrollToTop}
          sx={{
            position: 'fixed',
            bottom: 32,
            right: 32,
            boxShadow: 4,
            '&:hover': {
              boxShadow: 8,
              transform: 'scale(1.1)',
            },
            transition: 'all 0.3s',
          }}
        >
          <ArrowUpward />
        </Fab>
      )}
    </Box>
  );
};

export default AgenciesLandingPage;
